import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { SCENE_KEYS } from './scene-keys.js';
import { dataManager } from '../utils/data-manager.js';
import { netManager } from '../net/index.js';
import { fetchDailyQuests, claimQuest } from '../net/api.js';
import { DEMO_DAILY_QUESTS } from '../net/demo-content.js';
import { UI, makeButton, makeCloseButton } from '../utils/ui-button.js';

// ── Quests — onboarding goals + claimable daily quests (mouse/touch-driven). ──
// Two sections:
//   1. GOALS — the persistent onboarding objective chain (catch a beast / win 3 battles / reach
//      5,000 gold / order a card). These read the persisted PROGRESS counters and show live
//      progress bars; they auto-complete as the player plays (read-only here).
//   2. DAILY QUESTS — GET /quests/daily (falls back to demo set offline). A "CLAIM" button on a
//      completed quest calls POST /quests/claim, grants the Gold reward, marks it claimed and
//      persists. Every control is a mouse button; ESC also closes.

/** @typedef {import('../net/api.js').DailyQuest} DailyQuest */

/**
 * @typedef QuestsSceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

/** Onboarding objective chain — mirrors the world-hud OBJECTIVE_CHAIN (single source of truth
 *  for the progress derivation lives in dataManager.getProgress()). */
const GOALS = [
  {
    label: 'Catch your first beast',
    cur: (p) => Math.min(p.monstersCaught, 1),
    target: 1,
    reward: '⭐',
  },
  {
    label: 'Win 3 battles',
    cur: (p) => Math.min(p.battlesWon, 3),
    target: 3,
    reward: '⭐',
  },
  {
    label: 'Reach 5,000 Gold',
    cur: () => Math.min(dataManager.getGold(), 5000),
    target: 5000,
    reward: '⭐',
  },
  {
    label: 'Order a real card',
    cur: (p) => Math.min(p.cardsOrdered, 1),
    target: 1,
    reward: '⭐',
  },
];

export class QuestsScene extends BaseScene {
  /** @type {QuestsSceneData} */ #sceneData;
  /** @type {DailyQuest[]} */ #quests = [];
  /** @type {Phaser.GameObjects.Container} */ #dailyContainer;
  /** @type {Phaser.GameObjects.Text} */ #statusText;
  /** @type {boolean} */ #busy = false;
  /** @type {number} */ #panelLeft = 0;
  /** @type {number} */ #panelW = 0;
  /** @type {number} */ #dailyTop = 0;

  constructor() {
    super({ key: SCENE_KEYS.QUESTS_SCENE });
  }

  /** @param {QuestsSceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
    this.#quests = [];
    this.#busy = false;
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const { width, height } = this.scale;

    this.add.rectangle(0, 0, width, height, 0x05080d, 0.82).setOrigin(0);

    const panelW = Math.min(820, width - 60);
    const panelH = Math.min(620, height - 40);
    const cx = width / 2;
    const top = height / 2 - panelH / 2;
    const left = cx - panelW / 2;
    this.#panelLeft = left;
    this.#panelW = panelW;

    const panel = this.add.graphics();
    panel.fillStyle(UI.glass, 0.96);
    panel.fillRoundedRect(left, top, panelW, panelH, 18);
    panel.lineStyle(2, UI.emerald, 0.6);
    panel.strokeRoundedRect(left, top, panelW, panelH, 18);

    this.add
      .text(cx, top + 38, 'QUESTS', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '40px',
        color: UI.emeraldHex,
        stroke: '#05351f',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    makeCloseButton(this, left + panelW - 34, top + 34, () => this.#close());

    // ── GOALS section ──
    let y = top + 84;
    this.add.text(left + 30, y, 'GOALS', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '18px',
      color: UI.goldHex,
    });
    y += 34;
    const progress = dataManager.getProgress();
    GOALS.forEach((goal) => {
      const cur = goal.cur(progress);
      const done = cur >= goal.target;
      this.#goalRow(left + 30, y, panelW - 60, goal, cur, done);
      y += 52;
    });

    // ── DAILY QUESTS section ──
    y += 8;
    this.add.text(left + 30, y, 'DAILY QUESTS', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '18px',
      color: UI.goldHex,
    });
    this.#dailyTop = y + 30;
    this.#dailyContainer = this.add.container(0, 0);

    this.#statusText = this.add
      .text(cx, top + panelH - 28, 'Loading daily quests…', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '15px',
        color: UI.subHex,
      })
      .setOrigin(0.5);

    void this.#loadQuests();
    this.cameras.main.fadeIn(160, 0, 0, 0);
  }

  /** @param {number} x @param {number} y @param {number} w @param {object} goal @param {number} cur @param {boolean} done */
  #goalRow(x, y, w, goal, cur, done) {
    const g = this.add.graphics();
    g.fillStyle(0x0d1c14, 0.85);
    g.fillRoundedRect(x, y, w, 48, 10);
    g.lineStyle(2, done ? UI.gold : UI.glassStroke, done ? 0.9 : 0.7);
    g.strokeRoundedRect(x, y, w, 48, 10);

    this.add
      .text(x + 14, y + 10, `${done ? '✓ ' : ''}${goal.label}`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '18px',
        color: done ? UI.goldHex : UI.textHex,
      });

    // progress bar
    const barX = x + 14;
    const barY = y + 34;
    const barW = w - 160;
    const frac = Phaser.Math.Clamp(cur / goal.target, 0, 1);
    const bar = this.add.graphics();
    bar.fillStyle(0x0b1409, 0.9);
    bar.fillRoundedRect(barX, barY, barW, 7, 3.5);
    if (frac > 0) {
      bar.fillStyle(done ? UI.gold : UI.emerald, 1);
      bar.fillRoundedRect(barX, barY, Math.max(7, barW * frac), 7, 3.5);
    }

    this.add
      .text(x + w - 14, y + 24, done ? 'COMPLETE' : `${cur.toLocaleString()} / ${goal.target.toLocaleString()}`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '15px',
        color: done ? UI.goldHex : UI.subHex,
      })
      .setOrigin(1, 0.5);
  }

  async #loadQuests() {
    const wallet = netManager.wallet;
    const remote = await fetchDailyQuests(wallet);
    if (remote && remote.length > 0) {
      this.#quests = remote;
    } else {
      this.#quests = DEMO_DAILY_QUESTS;
    }
    // No persistent bottom status line — it's reused only for transient claim feedback.
    this.#statusText.setText('');
    this.#renderDaily();
  }

  #renderDaily() {
    this.#dailyContainer.removeAll(true);
    const x = this.#panelLeft + 30;
    const w = this.#panelW - 60;
    const rowH = 70;

    this.#quests.slice(0, 3).forEach((q, i) => {
      const y = this.#dailyTop + i * rowH;
      const claimed = !!q.progress?.claimedAt;
      const completed = !!q.progress?.completed;

      const card = this.add.graphics();
      card.fillStyle(0x0d1c14, 0.85);
      card.fillRoundedRect(x, y, w, rowH - 12, 10);
      card.lineStyle(2, completed && !claimed ? UI.gold : UI.glassStroke, 0.7);
      card.strokeRoundedRect(x, y, w, rowH - 12, 10);
      this.#dailyContainer.add(card);

      this.#dailyContainer.add(
        this.add.text(x + 14, y + 8, q.def.title, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '18px',
          color: UI.textHex,
        })
      );
      this.#dailyContainer.add(
        this.add.text(x + 14, y + 32, q.def.description, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '13px',
          color: UI.subHex,
          wordWrap: { width: w - 220 },
        })
      );
      this.#dailyContainer.add(
        this.add.text(x + 14, y + rowH - 26, `Reward: ${q.def.reward.gold} Gold · ${q.def.reward.xp} XP`, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '13px',
          color: UI.goldHex,
        })
      );

      // right-side action / state
      if (claimed) {
        this.#dailyContainer.add(
          this.add
            .text(x + w - 16, y + (rowH - 12) / 2, 'CLAIMED', {
              fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
              fontSize: '16px',
              color: '#888888',
            })
            .setOrigin(1, 0.5)
        );
      } else if (completed) {
        this.#dailyContainer.add(
          makeButton(this, {
            x: x + w - 80,
            y: y + (rowH - 12) / 2,
            width: 130,
            height: 40,
            label: 'CLAIM',
            primary: true,
            fontSize: 18,
            onClick: () => void this.#claim(q),
          })
        );
      } else {
        this.#dailyContainer.add(
          this.add
            .text(x + w - 16, y + (rowH - 12) / 2, 'IN PROGRESS', {
              fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
              fontSize: '15px',
              color: UI.subHex,
            })
            .setOrigin(1, 0.5)
        );
      }
    });
  }

  /** @param {DailyQuest} quest */
  async #claim(quest) {
    if (this.#busy) return;
    if (quest.progress?.claimedAt) {
      this.#statusText.setText('Already claimed.');
      return;
    }
    if (!quest.progress?.completed) {
      this.#statusText.setText('Quest not complete yet.');
      return;
    }
    this.#busy = true;
    this.#statusText.setText('Claiming…');
    const wallet = netManager.wallet;
    const result = await claimQuest(wallet, quest.def.id);
    if (result) {
      if (typeof result.gold === 'number') {
        dataManager.setGold(result.gold);
      } else {
        // offline-safe local grant so the reward is felt even without a server echo
        dataManager.setGold(dataManager.getGold() + (quest.def.reward?.gold ?? 0));
      }
      if (quest.progress) quest.progress.claimedAt = new Date().toISOString();
      this.#statusText.setText(`Claimed ${quest.def.reward.gold} Gold!`);
      this.#renderDaily();
    } else {
      this.#statusText.setText('Could not claim (offline).');
    }
    this.#busy = false;
  }

  update() {
    super.update();
    if (this._controls?.isInputLocked || this.#busy) return;
    if (this._controls?.wasBackKeyPressed?.()) {
      this.#close();
    }
  }

  #close() {
    this._controls.lockInput = true;
    this.scene.stop(SCENE_KEYS.QUESTS_SCENE);
    this.scene.resume(this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE);
  }
}
